import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Loader2 } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import { useLocation } from "@/context/LocationContext";
import { Stars } from "@/components/Stars";
import { ProductCard } from "@/components/ProductCard";
import { Footer } from "@/components/Footer";

export default function SellerProfile() {
  const { id } = useParams();
  const { loc } = useLocation();
  const [s, setS] = useState(null);

  useEffect(() => {
    const geo = loc ? `?lat=${loc.lat}&lng=${loc.lng}` : "";
    api.get(`/sellers/${id}${geo}`).then((r) => setS(r.data));
  }, [id, loc]);

  if (!s) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#1A3C2A]" /></div>;

  return (
    <div>
      <div className="h-48 sm:h-64 bg-[#E8E0CF] relative">
        {s.cover && <img src={imageUrl(s.cover)} alt="" className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-14 relative">
          <div className="w-28 h-28 rounded-3xl border-4 border-[#FAF7F2] bg-white overflow-hidden shadow-lg shrink-0">
            <img src={imageUrl(s.logo)} alt={s.shop_name} className="w-full h-full object-cover" />
          </div>
          <div className="sm:pb-2">
            <h1 data-testid="seller-shop-name" className="font-display text-2xl sm:text-3xl font-extrabold text-[#1A3C2A]">{s.shop_name}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              <span className="text-sm font-semibold text-[#E07A5F] uppercase tracking-wide">{s.category}</span>
              <span className="text-sm text-[#5A626A] flex items-center gap-1"><MapPin size={13} /> {s.area}{s.distance_miles != null ? ` · ${s.distance_miles} mi` : ""}</span>
              <Stars rating={s.rating} count={s.review_count} />
            </div>
          </div>
        </div>
        <p className="text-sm text-[#5A626A] mt-4 max-w-2xl leading-relaxed">{s.description}</p>

        <h2 className="font-display text-xl font-bold text-[#1A3C2A] mt-8 mb-4">Products ({s.products?.length || 0})</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
          {s.products?.map((p, i) => <ProductCard key={p.id} product={{ ...p, seller_name: s.shop_name, seller_area: s.area }} index={i} />)}
        </div>

        {s.reviews?.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-xl font-bold text-[#1A3C2A] mb-4">Customer reviews</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {s.reviews.map((r) => (
                <div key={r.id} className="bg-white border border-[#E5DEC9] rounded-2xl p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-[#22252A]">{r.user_name}</span>
                    <Stars rating={r.rating} showNumber={false} />
                  </div>
                  {r.comment && <p className="text-sm text-[#5A626A] mt-1">{r.comment}</p>}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
      <div className="mt-16"><Footer /></div>
    </div>
  );
}
